import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErpAlert, ErpButton, ErpDiv, ErpFormInput, ErpModal, ErpSpan } from '@/components/erp';
import { useAppDispatch } from '@/redux/hooks';
import { apiSlice } from '@/redux/api/apiSlice';
import { useBusinessTime, useChangeBusinessClockMutation } from '@/redux/api/businessClockApi';

/** Calendar arithmetic, independent of browser timezone; month-end clamps instead of overflowing. */
export function shiftBusinessTime(value: string, amount: number, unit: 'day' | 'month' | 'year'): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return value;
  const [, y, m, d, h, minute] = match;
  const date = new Date(Date.UTC(+y, +m - 1, +d, +h, +minute));
  if (unit === 'day') date.setUTCDate(date.getUTCDate() + amount);
  else {
    const day = date.getUTCDate();
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + amount * (unit === 'year' ? 12 : 1));
    const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
    date.setUTCDate(Math.min(day, last));
  }
  return date.toISOString().slice(0, 16);
}

export default function BusinessClockButton({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data, companyId } = useBusinessTime();
  const [change] = useChangeBusinessClockMutation();
  const [open, setOpen] = useState(false), [at, setAt] = useState(''), [password, setPassword] = useState('');
  const [revision, setRevision] = useState<string | null>(null), [editingCompany, setEditingCompany] = useState<string | null>(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  useEffect(() => { setOpen(false); setPassword(''); setError(''); }, [companyId]);
  if (!canManage) return null;
  const close = () => { if (!busy) { setOpen(false); setPassword(''); setError(''); } };
  const apply = async (enabled: boolean) => {
    if (busy || !password || !editingCompany || (enabled && !at)) return;
    setBusy(true); setError('');
    try {
      await change({ companyId: editingCompany, enabled, at: enabled ? at : null, password, revision }).unwrap();
      setOpen(false); setPassword('');
      dispatch(apiSlice.util.resetApiState());
    } catch (err: any) {
      setError(t(err?.status === 409 ? 'Business_Clock_Conflict' : err?.data?.errors?.password ? 'Business_Reset_Password_Error' : 'Business_Clock_Failed'));
    } finally { setBusy(false); }
  };
  return <>
    <ErpButton variant="outline" disabled={!data || busy} onClick={() => {
      if (!data || !companyId) return;
      setAt(data.localDatetime); setRevision(data.revision); setEditingCompany(companyId); setError(''); setOpen(true);
    }}>{t('Business_Clock_Title')}</ErpButton>
    <ErpModal show={open} title={t('Business_Clock_Title')} noFooter onHide={close}
      onUpdateModelValue={value => { if (!value) close(); }} busy={busy} noCloseOnBackdrop={busy} noCloseOnEsc={busy} hideHeaderClose={busy}>
      <ErpDiv className="flex min-w-0 flex-col gap-4">
        <ErpAlert variant="warning">{t('Business_Clock_Warning')}</ErpAlert>
        <ErpDiv>{t('Business_Clock_Real')}: <ErpSpan dir="ltr" className="inline-block">{data?.realNow}</ErpSpan></ErpDiv>
        <ErpDiv>{t('Business_Clock_Zone')}: {data?.timezone}</ErpDiv>
        <ErpFormInput type="datetime-local" label={t('Business_Clock_Date')} value={at} onChange={setAt} disabled={busy}
          min="1900-01-01T00:00" max="2200-12-31T23:59" required />
        <ErpDiv className="flex flex-wrap gap-2">
          {(['day', 'month', 'year'] as const).flatMap(unit => [-1, 1].map(amount =>
            <ErpButton key={`${unit}${amount}`} variant="outline" size="sm" disabled={busy || !at}
              onClick={() => setAt(shiftBusinessTime(at, amount, unit))}>{t(`Business_Clock_${unit}_${amount < 0 ? 'back' : 'next'}`)}</ErpButton>))}
        </ErpDiv>
        <ErpFormInput type="password" autoComplete="current-password" label={t('Business_Reset_Password')} value={password} onChange={setPassword} disabled={busy} />
        {error && <ErpAlert variant="danger">{error}</ErpAlert>}
        <ErpDiv className="flex flex-wrap justify-end gap-2">
          <ErpButton variant="outline" disabled={busy} onClick={close}>{t('Cancel')}</ErpButton>
          <ErpButton variant="outline" disabled={busy || !password || !data?.enabled} onClick={() => apply(false)}>{t('Business_Clock_Restore')}</ErpButton>
          <ErpButton disabled={busy || !password || !at} onClick={() => apply(true)}>{t('Business_Clock_Apply')}</ErpButton>
        </ErpDiv>
      </ErpDiv>
    </ErpModal>
  </>;
}
